import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAxiosInstance from "../../hooks/useAxios";
import Input from "../../components/input";
import { toast } from "react-toastify";
import Select from "react-select";
import { MdEdit } from "react-icons/md";
import { Close, Delete, Edit } from "@mui/icons-material";
import { v4 as uuid } from "uuid";

const UserPreferencesBulkEdit = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [organizations, setOrganizations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [searchOrg, setSearchOrg] = useState("");
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [notificationPreferencesStructure, setNotificationPreferencesStructure] = useState([]);
  const [notificationStructureLoading, setNotificationStructureLoading] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState({});
  const [userPreferences, setUserPreferences] = useState({
    preferred_notebook_language: "en",
    data_sources: {
      preferred: [],
      non_preferred: []
    },
    pinned_workflows: []
  });
  const [preferredSources, setPreferredSources] = useState([]);
  const [nonPreferredSources, setNonPreferredSources] = useState([]);
  const [selectedPinnedWorkflows, setSelectedPinnedWorkflows] = useState([]);
  const [dashboardTiles, setDashboardTiles] = useState([]);
  const [dashboardCardLoading, setDashboardCardLoading] = useState(false);

  const [notificationLoading, setNotificationLoading] = useState(false);
  const [userPreferencesLoading, setUserPreferencesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orgPreferencesSaving, setOrgPreferencesSaving] = useState(false);

  const axiosInstance = useAxiosInstance();

  const languageOptions = [
    { label: "English", value: "EN" },
    { label: "French", value: "FR" },
    { label: "German", value: "DE" },
    { label: "Hebrew", value: "IW" },
    { label: "Japanese", value: "JA" },
    { label: "Korean", value: "KO" },
    { label: "Mandarin Chinese", value: "zh-CN" },
    { label: "Spanish", value: "ES" },
    { label: "Arabic", value: "AR" },
  ].map((it) => ({ label: it.label, value: it.value.toLowerCase() }));

  const getOrgImage = (img, name) => {
    if (img === null || img === "") {
      return `https://wklogo.blob.core.windows.net/logos-small/${name[0]?.toUpperCase()}.png`;
    } else {
      return img;
    }
  };

  const getFilteredOrgs = (organizations, searchOrg) => {
    if (!searchOrg) {
      return organizations;
    }
    return organizations?.filter(
      (item) =>
        item?.name?.toLowerCase().includes(searchOrg?.toLowerCase()) ||
        item?.label?.toLowerCase().includes(searchOrg?.toLowerCase())
    );
  };

  useEffect(() => {
    setOrgLoading(true);
    axiosInstance
      .get("admin/organisations/")
      .then((res) => {
        setOrganizations(res.data.organisations || []);
        setOrgLoading(false);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Something went wrong while getting organizations");
        setOrgLoading(false);
      });
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      setUsersLoading(true);
      axiosInstance
        .get(`/admin/get-org-users/?org_ids=${selectedOrg.org_id}&pgsz=999999999`)
        .then((res) => {
          setOrgUsers(res.data.users || []);
          setUsersLoading(false);
        })
        .catch((err) => {
          console.log(err);
          toast.error("Something went wrong while getting users");
          setUsersLoading(false);
        });
    }
  }, [selectedOrg]);

  const handleEditOrg = (org) => {
    setSelectedOrg(org);
    setSearchParams({ org_name: org.name });
    setBulkEditMode(true);
    fetchDashboardTiles(org.org_id);
    fetchNotificationPreferencesStructure(org);
  };

  const handleBackToOrgList = () => {
    setSelectedOrg(null);
    setBulkEditMode(false);
    setOrgUsers([]);
    setSearchParams({});
    setNotificationPreferences({});
    setUserPreferences({
      preferred_notebook_language: "en",
      data_sources: { preferred: [], non_preferred: [] },
      pinned_workflows: []
    });
    setPreferredSources([]);
    setNonPreferredSources([]);
    setSelectedPinnedWorkflows([]);
  };

  const fetchDashboardTiles = (orgId) => {
    setDashboardCardLoading(true);
    axiosInstance
      .post(`accounts/sandbox-dashboard/`, {
        "org_pk": String(orgId),
        "is_admin": true
      })
      .then((res) => {
        const data = res.data?.data || [];
        const features = res.data?.features || {};

        // Process tiles similar to original component
        data?.forEach((tile) => (tile.tileType = "report"));
        if (features && Object.keys(features)?.length) {
          const additionalTiles = Object.values(features);
          const codes = additionalTiles.map((e) => e.code);
          const alreadyInCards = data.find((e) => codes.includes(e.code));
          if (alreadyInCards) {
            alreadyInCards.tileType = "feature";
          } else {
            additionalTiles.forEach((tile) => {
              tile.tileType = "feature";
            });
            data.push(...additionalTiles);
          }
        }

        const cardRes = data?.sort((a, b) => a?.order_preference - b?.order_preference) || [];
        setDashboardTiles(cardRes.filter((c) => !c.subscription.includes("Hidden")));
        setDashboardCardLoading(false);
      })
      .catch((err) => {
        setDashboardCardLoading(false);
        console.error("Error fetching dashboard tiles:", err);
      });
  };

  const fetchNotificationPreferencesStructure = async (org) => {
    setNotificationStructureLoading(true);
    try {
       const notificationRes = await axiosInstance.get(`/accounts/notification-preferences/`);
      setNotificationPreferencesStructure(notificationRes.data?.notification_preferences || []);
    } catch (errorWithoutUser) {
      try {
         const userRes = await axiosInstance.get(`/admin/get-org-users/?org_ids=${org.org_id}&pgsz=1`);
        const users = userRes.data.users || [];
        if (users.length > 0) {
          const firstUserId = users[0].user_id;
          const notificationRes = await axiosInstance.get(`/accounts/notification-preferences/?user__id=${firstUserId}`);
          setNotificationPreferencesStructure(notificationRes.data?.notification_preferences || []);
        } else {
          setNotificationPreferencesStructure([]);
        }
      } catch (errorWithUser) {
        console.error("Error fetching notification preferences structure:", errorWithUser);
        setNotificationPreferencesStructure([]);
      }
    } finally {
      setNotificationStructureLoading(false);
    }
  };

  const saveOrgPreferences = async (partialUpdate) => {
    if (!selectedOrg) return;
    try {
      setOrgPreferencesSaving(true);
      const payload = { org_id: selectedOrg.org_id, ...partialUpdate };
      const res = await axiosInstance.post("/accounts/org-preferences/update/", payload);
      if (res.status === 200) {
        toast.success("Updated");
      } else {
        toast.error("Update failed");
      }
    } catch (error) {
      toast.error("Update failed");
    } finally {
      setOrgPreferencesSaving(false);
    }
  };

  const handleBulkSave = async () => {
    if (!selectedOrg) {
      toast.error("No organization or users selected");
      return;
    }

    setSaving(true);

    try {
      const promises = [];

      if (Object.keys(notificationPreferences).length > 0) {
        orgUsers.forEach(user => {
          promises.push(
            axiosInstance.post("/accounts/notification-preferences/", {
              user__id: user.user_id,
              notification_preferences: Object.entries(notificationPreferences).map(([typeCode, channels]) => ({
                type_code: typeCode,
                channels: Object.entries(channels).map(([channelCode, enabled]) => ({
                  channel_code: channelCode,
                  enabled: enabled
                }))
              }))
            })
          );
        });
      }

      await Promise.all(promises);
      toast.success(`Successfully updated notification preferences for ${orgUsers.length} users`);
      handleBackToOrgList();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update some preferences");
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationChange = (type_code, channel_code, enabled) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [type_code]: {
        ...prev[type_code],
        [channel_code]: enabled
      }
    }));
  };

  const handleUserPreferenceChange = (key, value) => {
    setUserPreferences(prev => ({
      ...prev,
      [key]: value
    }));
    if (key === 'preferred_notebook_language') {
      saveOrgPreferences({ preferred_notebook_language: value });
    }
  };

  const handlePinnedWorkflowsChange = (selectedWorkflows) => {
    setSelectedPinnedWorkflows(selectedWorkflows);
  };

  const addPreferredSource = () => {
    setPreferredSources(prev => [...prev, { value: "", id: Date.now() }]);
  };

  const addNonPreferredSource = () => {
    setNonPreferredSources(prev => [...prev, { value: "", id: Date.now() }]);
  };

  const updatePreferredSource = (id, value) => {
    setPreferredSources(prev =>
      prev.map(source => source.id === id ? { ...source, value } : source)
    );
  };

  const updateNonPreferredSource = (id, value) => {
    setNonPreferredSources(prev =>
      prev.map(source => source.id === id ? { ...source, value } : source)
    );
  };

  const removePreferredSource = (id) => {
    setPreferredSources(prev => prev.filter(source => source.id !== id));
  };

  const removeNonPreferredSource = (id) => {
    setNonPreferredSources(prev => prev.filter(source => source.id !== id));
  };

  if (bulkEditMode && selectedOrg) {
    return (
      <div>
        <p className="text-xl font-semibold p-3 mt-5 bg-white border rounded-xl mb-3">
          Bulk Edit User Preferences - {selectedOrg.name}
        </p>
        <p className="text-sm text-gray-600 mb-4 px-3">
          Users: {usersLoading ? "Loading..." : `${orgUsers.length} user${orgUsers.length !== 1 ? 's' : ''}`}
        </p>

        {/* Notification Preferences Section */}
        <div className="p-3 border bg-white rounded-xl mb-4">
          {notificationStructureLoading ? (
            <p>Loading notification preferences...</p>
          ) : (
            notificationPreferencesStructure?.map((notification) => (
              notification?.meta?.hidden_on_frontend ? (
                null ) : (
              <div key={notification.type_code} className="flex justify-between mb-3">
                <div>
                  <p className="text-base font-semibold">
                    {notification.type_label}
                  </p>
                  <p className="text-sm font-normal">
                    {notification.description}
                  </p>
                </div>
                <div className="flex gap-x-2 items-center">
                  {notification.channels?.map((channel) => (
                    <span key={channel.channel_code} className="text-xs font-semibold">
                      {channel.channel_code === 'EMAIL' ? 'Email' : 'In-app'}{" "}
                      <input
                        type="checkbox"
                        onChange={(e) => handleNotificationChange(notification.type_code, channel.channel_code, e.target.checked)}
                      />
                    </span>
                  ))}
                </div>
              </div>
            )))
          )}
        </div>

        {/* User Preferences Section */}
        <div className="p-3 mt-4 border bg-white rounded mb-4">
          <div className="mb-4">
            <div className="flex gap-x-3 items-center w-full">
              <p className="text-base font-semibold mb-3 w-full">Preferred</p>
              <button
                onClick={() => {
                  setPreferredSources((prev) => [
                    ...prev,
                    { value: "", id: uuid() },
                  ]);
                }}
                className="px-3 py-1 bg-black rounded text-white"
              >
                add
              </button>
            </div>
            <TagRender
              preferedSources={preferredSources}
              setPreferedSources={setPreferredSources}
              axiosInstance={axiosInstance}
              nonPreferedSources={nonPreferredSources}
              orgId={selectedOrg.org_id}
            />
          </div>
          <div className="mb-4 justify-between items-center">
            <div className="flex gap-x-3 items-center">
              <p className="text-base font-semibold mb-3 w-full">
                Non Preferred
              </p>
              <button
                onClick={() => {
                  setNonPreferredSources((prev) => [
                    ...prev,
                    { value: "", id: uuid() },
                  ]);
                }}
                className="px-3 py-1 bg-black rounded text-white"
              >
                add
              </button>
            </div>
            <TagRender
              preferedSources={nonPreferredSources}
              setPreferedSources={setNonPreferredSources}
              axiosInstance={axiosInstance}
              nonPreferedSources={preferredSources}
              is_invert
              orgId={selectedOrg.org_id}
            />
          </div>

          <div className="flex mb-4 justify-between items-center">
            <p className="text-base font-semibold mb-3 ">Pinned workflows</p>
            <button
              onClick={() => {
                setSelectedPinnedWorkflows(
                  selectedPinnedWorkflows.length > 0
                    ? selectedPinnedWorkflows
                    : dashboardTiles
                        .filter((_, idx) => idx < 4)
                        .map((card) => ({
                          value: card.code,
                          label: card.name,
                        }))
                );
              }}
              className="px-3 py-2 rounded-lg bg-black text-white font-semibold text-sm"
            >
              Edit
            </button>
          </div>
          {selectedPinnedWorkflows.length > 0 ? (
            <div className="flex justify-between items-center gap-x-2 mb-4">
              <Select
                isMulti
                className="w-full"
                options={dashboardTiles.map((elem) => ({
                  label: elem.name,
                  value: elem.code,
                }))}
                onChange={handlePinnedWorkflowsChange}
                value={selectedPinnedWorkflows}
              />
              <button
                onClick={() => {
                  saveOrgPreferences({ pinned_workflows: selectedPinnedWorkflows.map((w) => w.value) });
                }}
                disabled={orgPreferencesSaving}
                className="px-3 py-2 rounded-lg bg-black text-white font-semibold text-sm disabled:bg-slate-200"
              >
                {orgPreferencesSaving ? "Saving..." : "Save and close"}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-x-3 mb-4">
              {dashboardTiles
                .filter((_, idx) => idx < 4)
                .map((card) => ({ value: card.code, label: card.name }))
                ?.map((card) => {
                  const item = dashboardTiles.find(
                    (c) => card.value === c?.code && !c.subscription.includes("Hidden")
                  );
                  const icon = item?.icon;
                  const mainTitle = item?.name;
                  return (
                    <div className="px-3 py-3 flex items-center gap-x-2 w-full border rounded-lg text-base font-medium">
                      <img className="h-8" src={icon} />
                      {mainTitle}
                    </div>
                  );
                })}
            </div>
          )}

          <p className="text-base font-semibold mb-3 mt-5">
            Language preferences
          </p>
          <select
            value={userPreferences.preferred_notebook_language}
            onChange={(e) => handleUserPreferenceChange('preferred_notebook_language', e.target.value)}
            className="border rounded-lg px-3 py-2 w-full focus:ring-0 focus:outline-gray-400"
          >
            <option value={""}>Choose</option>
            {languageOptions.map((it) => (
              <option key={it.label} value={it.value}>
                {it.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end space-x-4 mt-4">
          <button
            onClick={handleBackToOrgList}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleBulkSave}
            className="px-6 py-2 bg-black text-white rounded-lg disabled:bg-gray-400"
            disabled={saving || orgUsers.length === 0}
          >
            {saving ? "Saving..." : `Save for ${orgUsers.length} User${orgUsers.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full">
      <div className="text-2xl font-semibold mb-4">Organization User Preferences - Bulk Edit</div>

      <div className="mt-4">
        <div className="flex justify-between items-center w-full mb-4">
          <p className="text-sm font-semibold">Select Organization to Edit</p>
        </div>

        <Input
          className="w-full mb-4"
          placeholder="Search organizations"
          value={searchOrg}
          onChange={(e) => setSearchOrg(e.target.value)}
        />

        {orgLoading ? (
          <p className="mt-4">Loading organizations...</p>
        ) : (
          <div className="overflow-x-auto h-[70vh] overflow-y-auto mt-4 border rounded-lg">
            <table className="w-full">
              <thead className="border-b text-left">
                <th className="px-4 py-2 text-gray-700 text-sm">Select</th>
                <th className="px-4 py-2 text-gray-700 text-sm">Name</th>
                <th className="px-4 py-2 text-gray-700 text-sm">Image</th>
                <th className="px-4 py-2 text-gray-700 text-sm">ID</th>
                <th className="px-4 py-2 text-gray-700 text-sm">Label</th>
                <th className="px-4 py-2 text-gray-700 text-sm">Account Type</th>
              </thead>
              <tbody className="divide-y">
                {(getFilteredOrgs(organizations, searchOrg) || []).map((org, index) => (
                  <tr key={index} className="bg-white odd:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-2 text-gray-900">
                      <button
                        onClick={() => handleEditOrg(org)}
                        className="bg-black text-white px-3 py-1 rounded-lg text-sm hover:bg-gray-800"
                      >
                        <MdEdit />
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-gray-900">
                      {org.name}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-gray-900">
                      <div
                        style={{
                          backgroundImage: `url('${getOrgImage(org?.org_img, org?.name)}')`
                        }}
                        className="h-8 w-8 bg-center bg-no-repeat bg-contain"
                      />
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-gray-900">
                      {org.org_id}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-gray-900">
                      {org.label}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-gray-900">
                      {org?.account_type?.name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const TagRender = ({
  preferedSources,
  setPreferedSources,
  axiosInstance,
  nonPreferedSources,
  is_invert = false,
  orgId,
}) => {
  const [saving, setSaving] = React.useState(false);
  const saveOrgPreferences = async (partialUpdate) => {
    try {
      setSaving(true);
      const res = await axiosInstance.post("/accounts/org-preferences/update/", { org_id: orgId, ...partialUpdate });
      if (res.status === 200) {
        toast.success("Updated");
      } else {
        toast.error("Update failed");
      }
    } catch (error) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex flex-wrap w-full">
      {preferedSources.map((it) => {
        if (it.isEditOn) {
          return (
            <div key={it.id}>
              <input
                value={it.value}
                onChange={(e) => {
                  setPreferedSources((prev) => {
                    const source = JSON.parse(
                      JSON.stringify(prev.find((item) => item.id === it.id))
                    );
                    source["value"] = e.target.value;
                    let sources = [];
                    prev.forEach((item) => {
                      if (item.id === it.id) {
                        sources.push(source);
                      } else {
                        sources.push(item);
                      }
                    });
                    return sources;
                  });
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    setPreferedSources((prev) => {
                      const source = JSON.parse(
                        JSON.stringify(prev.find((item) => item.id === it.id))
                      );
                      source["isEditOn"] = false;
                      let sources = [];
                      prev.forEach((item) => {
                        if (item.id === it.id) {
                          sources.push(source);
                        } else {
                          sources.push(item);
                        }
                      });
                      return sources;
                    });
                    await saveOrgPreferences({
                      data_sources: {
                        preferred: is_invert
                          ? nonPreferedSources.map((it) => it.value)
                          : preferedSources.map((it) => it.value),
                        non_preferred: is_invert
                          ? preferedSources.map((it) => it.value)
                          : nonPreferedSources.map((it) => it.value),
                      },
                    });
                  }
                }}
              />
              <Close
                onClick={() => {
                  setPreferedSources((prev) => {
                    const source = JSON.parse(
                      JSON.stringify(prev.find((item) => item.id === it.id))
                    );
                    source["isEditOn"] = false;
                    let sources = [];
                    prev.forEach((item) => {
                      if (item.id === it.id) {
                        sources.push(source);
                      } else {
                        sources.push(item);
                      }
                    });
                    return sources;
                  });
                }}
              />
            </div>
          );
        }

        return (
          <div
            key={it.id}
            className="px-4 py-2 bg-slate-100 text-sm font-semibold flex w-max rounded-3xl"
          >
            {it.value}{" "}
            <div className="flex gap-x-[2px] ml-2 items-center">
              <Edit
                className="cursor-pointer"
                onClick={() => {
                  setPreferedSources((prev) => {
                    const source = JSON.parse(
                      JSON.stringify(prev.find((item) => item.id === it.id))
                    );
                    source["isEditOn"] = true;
                    let sources = [];
                    prev.forEach((item) => {
                      if (item.id === it.id) {
                        sources.push(source);
                      } else {
                        sources.push(item);
                      }
                    });
                    return sources;
                  });
                }}
                style={{ height: "16px" }}
              />{" "}
              <Delete
                className="cursor-pointer"
                onClick={async () => {
                  setPreferedSources((prev) => {
                    let sources = [];
                    prev.forEach((item) => {
                      if (item.id === it.id) {
                      } else {
                        sources.push(item);
                      }
                    });
                    return sources;
                  });
                  await saveOrgPreferences({
                    data_sources: {
                      preferred: is_invert
                        ? nonPreferedSources
                            .filter((_it) => _it.id !== it.id)
                            .map((it) => it.value)
                        : preferedSources
                            .filter((_it) => _it.id !== it.id)
                            .map((it) => it.value),
                      non_preferred: is_invert
                        ? preferedSources
                            .filter((_it) => _it.id !== it.id)
                            .map((it) => it.value)
                        : nonPreferedSources
                            .filter((_it) => _it.id !== it.id)
                            .map((it) => it.value),
                    },
                  });
                }}
                style={{ height: "16px" }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default UserPreferencesBulkEdit;
